import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { Meta, MetaDefinition, Title } from '@angular/platform-browser';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { TranslateService } from '@ngx-translate/core';
import { isEqual } from 'lodash-es';
import { Subject, combineLatest } from 'rxjs';
import { distinctUntilChanged, map, switchMap, tap } from 'rxjs/operators';

import { SeoAttributes } from 'ish-core/models/seo-attributes/seo-attributes.model';
import { DomService } from 'ish-core/utils/dom/dom.service';
import { mapToPayloadProperty } from 'ish-core/utils/operators';

import { getDotCmsSeoAttributesFromPageMeta } from '../../utils/seo/dot-cms-seo-utils';
import { loadDotCmsPageSuccess } from '../dotcms-pages/dotcms-pages.action';

@Injectable()
export class DotcmsSeoEffects {
  constructor(
    private actions$: Actions,
    private metaService: Meta,
    private titleService: Title,
    private translate: TranslateService,
    @Inject(DOCUMENT) private doc: Document,
    private domService: DomService
  ) {}

  private pageTitle$ = new Subject<string>();

  seoAttributes$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(loadDotCmsPageSuccess),
        mapToPayloadProperty('page'),
        map(page => getDotCmsSeoAttributesFromPageMeta(page.metaInfo)),
        distinctUntilChanged(isEqual),
        tap(seoAttributes => {
          this.setSeoAttributes(seoAttributes);
        })
      ),
    { dispatch: false }
  );

  seoTitle$ = createEffect(
    () =>
      this.pageTitle$.pipe(
        switchMap(title => combineLatest([this.translate.get(title), this.translate.get('seo.applicationName')])),
        map(([title, application]) => `${title} | ${application}`),
        tap(title => {
          this.titleService.setTitle(title);
        })
      ),
    { dispatch: false }
  );

  private setCanonicalLink(url: string) {
    // the canonical URL of a production system should always be with 'https:'
    // even though the PWA SSR container itself is usually not deployed in an SSL environment so the URLs need manual adaption
    const canonicalUrl = encodeURI(url.replace('http:', 'https:'));
    const canonicalLink = this.domService.getOrCreateElement('link[rel="canonical"]', 'link', this.doc.head);

    this.domService.setAttribute(canonicalLink, 'rel', 'canonical');
    this.domService.setAttribute(canonicalLink, 'href', canonicalUrl);

    this.addOrModifyTag({ property: 'og:url', content: canonicalUrl });
  }

  private setSeoAttributes(seoAttributes: SeoAttributes) {
    Object.entries(seoAttributes)
      .filter(([, v]) => !!v)
      .forEach(([key, value]) => {
        switch (key) {
          case 'title':
            this.pageTitle$.next(value);
            return;
          case 'canonicalUrl':
            this.setCanonicalLink(value);
            return;

          case 'robots':
            const content = value
              .split(',')
              .filter(e => !e.match(/snippet/))
              .join(',');
            this.addOrModifyTag({ name: key, content });
            if (value.match(/snippet/)) this.metaService.addTag({ name: key, content: 'max-snippet: -1' });
            return;
        }
        if (key.startsWith('og:')) this.addOrModifyTag({ property: key, content: value });
        else this.addOrModifyTag({ name: key, content: value });
      });
  }

  private addOrModifyTag(tag: MetaDefinition) {
    if (!this.metaService.updateTag(tag)) {
      this.metaService.addTag(tag);
    }
  }
}
